// The renderer for everything stored as prose: a row's note, a comment on it, a
// record's description.
//
// It walks the AST from lib/markdown.ts into React elements. **Never
// `dangerouslySetInnerHTML`** — a note can be written by a client from their own
// seat, and the team is who reads it back.
//
// It replaced `MentionText`, which did half of this — mentions, no structure — and
// is gone: one renderer for every prose field means an agent or a person can learn
// the rules once. A body with no markdown in it renders exactly as MentionText drew
// it, a single paragraph with its line breaks kept.

import type { ReactNode } from 'react'

import { parseInline, parseMarkdown, type Inline } from '../lib/markdown'
import { EntityLink } from './EntityLink'

function inline(src: string, key: string): ReactNode[] {
  return parseInline(src).map((seg: Inline, i) => {
    const k = `${key}-${i}`
    switch (seg.type) {
      case 'mention':
        return <EntityLink key={k} id={seg.id} label={seg.label} />
      case 'strong':
        return (
          <strong key={k} style={{ fontWeight: 600 }}>
            {seg.text}
          </strong>
        )
      case 'em':
        return <em key={k}>{seg.text}</em>
      case 'del':
        return (
          <span key={k} style={{ textDecoration: 'line-through', opacity: 0.7 }}>
            {seg.text}
          </span>
        )
      case 'code':
        return (
          <code key={k} className="md-code">
            {seg.text}
          </code>
        )
      case 'link':
        return (
          <a
            key={k}
            href={seg.href}
            target="_blank"
            rel="noreferrer noopener"
            style={{ color: 'var(--marine)', textDecoration: 'underline' }}
          >
            {seg.text}
          </a>
        )
      default:
        return <span key={k}>{seg.text}</span>
    }
  })
}

export function Markdown({ body, className = '' }: { body: string; className?: string }) {
  if (!body.trim()) return null
  const blocks = parseMarkdown(body)

  return (
    <div className={`md ${className}`}>
      {blocks.map((b, i) => {
        const k = `b${i}`
        switch (b.type) {
          case 'h': {
            const Tag = (['h3', 'h4', 'h5'] as const)[b.level - 1]!
            return <Tag key={k}>{inline(b.text, k)}</Tag>
          }
          case 'ul':
            return (
              <ul key={k}>
                {b.items.map((it, j) => (
                  <li key={`${k}-${j}`}>{inline(it, `${k}-${j}`)}</li>
                ))}
              </ul>
            )
          case 'ol':
            return (
              <ol key={k} start={b.start}>
                {b.items.map((it, j) => (
                  <li key={`${k}-${j}`}>{inline(it, `${k}-${j}`)}</li>
                ))}
              </ol>
            )
          case 'quote':
            return <blockquote key={k}>{inline(b.text, k)}</blockquote>
          case 'code':
            return (
              <pre key={k}>
                <code>{b.text}</code>
              </pre>
            )
          case 'hr':
            return <hr key={k} />
          default:
            return <p key={k}>{inline(b.text, k)}</p>
        }
      })}
    </div>
  )
}
